import * as React from "react";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import { Modal } from "../../../ui/components/Modal";
import type { OrganizationScopedRole } from "../../lib/api/users";
import * as userApi from "../../lib/api/users";
import { getApiErrorMessage } from "../../lib/api/api-error";
import { OrganizationPickerSelect } from "./OrganizationPickerSelect";
import type { InvitableOrganization } from "./OrganizationPickerSelect";
import { useI18n } from "../../lib/i18n";
import * as confirmStyles from "../organizations/OrganizationDetailPage.css";
import * as fieldStyles from "./AddUserToOrganizationModal.css";

export type { InvitableOrganization } from "./OrganizationPickerSelect";

function formatRoleLabel(role: string): string {
  return role
    .replace(/^pro:/, "")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function AddUserToOrganizationModal({
  open,
  setOpen,
  userId,
  userName,
  userEmail,
  token,
  organizations,
  isSuperAdmin,
  isAlreadyMember,
  onAdded,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  userId: string;
  userName: string;
  userEmail: string;
  token: string | null;
  organizations: InvitableOrganization[];
  isSuperAdmin: boolean;
  isAlreadyMember: (orgId: string) => boolean;
  onAdded?: () => void;
}) {
  const { t, tx } = useI18n();
  const [orgId, setOrgId] = React.useState("");
  const [roleId, setRoleId] = React.useState("");
  const [roles, setRoles] = React.useState<OrganizationScopedRole[]>([]);
  const [rolesLoading, setRolesLoading] = React.useState(false);
  const [pendingAction, setPendingAction] = React.useState<
    "invite" | "add" | null
  >(null);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);

  const loading = pendingAction !== null;

  const selectedOrg = organizations.find((o) => o.id === orgId);
  const alreadyMember = orgId ? isAlreadyMember(orgId) : false;

  React.useEffect(() => {
    if (!open) {
      setOrgId("");
      setRoleId("");
      setRoles([]);
      setRolesLoading(false);
      setError(null);
      setSuccess(null);
      setPendingAction(null);
      return;
    }
    if (!orgId && organizations.length === 1) {
      setOrgId(organizations[0].id);
    }
  }, [open, organizations, orgId]);

  React.useEffect(() => {
    if (!open || !token || !orgId) {
      setRoles([]);
      setRoleId("");
      return;
    }

    let cancelled = false;
    setRolesLoading(true);
    void userApi
      .fetchOrganizationRoles(token, orgId)
      .then((list) => {
        if (cancelled) return;
        setRoles(list);
        const member =
          list.find(
            (r) =>
              r.systemKey === "member" ||
              r.name === "member" ||
              r.name === "pro:member",
          ) ?? list[0];
        setRoleId(member?.id ?? "");
      })
      .catch(() => {
        if (cancelled) return;
        setRoles([]);
        setRoleId("");
      })
      .finally(() => {
        if (!cancelled) setRolesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, token, orgId]);

  const canSubmit =
    Boolean(token) &&
    Boolean(orgId) &&
    Boolean(roleId) &&
    roles.length > 0 &&
    !alreadyMember &&
    !rolesLoading;

  const handleAlreadyMemberError = () => {
    setError(
      selectedOrg
        ? `${userName} is already a member of ${selectedOrg.name}.`
        : "This user is already a member of the selected organization.",
    );
  };

  const handleInvite = async () => {
    if (!token) {
      setError("Session expired. Please sign in again.");
      return;
    }
    if (!orgId || !roleId) {
      setError("Select an organization and role");
      return;
    }
    if (alreadyMember) {
      handleAlreadyMemberError();
      return;
    }

    setPendingAction("invite");
    setError(null);
    setSuccess(null);
    try {
      await userApi.createOrganizationInvite(token, orgId, {
        email: userEmail.trim(),
        roleId,
      });
      setSuccess(
        selectedOrg
          ? `Invitation sent for ${userName} to join ${selectedOrg.name}.`
          : "Invitation sent.",
      );
      onAdded?.();
      setOpen(false);
    } catch (e) {
      const message = getApiErrorMessage(e, "Failed to send invitation");
      if (message.toLowerCase().includes("already a member")) {
        handleAlreadyMemberError();
        return;
      }
      setError(message);
    } finally {
      setPendingAction(null);
    }
  };

  const handleAddNow = async () => {
    if (!token) {
      setError("Session expired. Please sign in again.");
      return;
    }
    if (!orgId || !roleId) {
      setError("Select an organization and role");
      return;
    }
    if (alreadyMember) {
      handleAlreadyMemberError();
      return;
    }

    setPendingAction("add");
    setError(null);
    setSuccess(null);
    try {
      await userApi.addOrganizationMember(token, orgId, {
        userId,
        roleId,
      });
      onAdded?.();
      setOpen(false);
    } catch (e) {
      const message = getApiErrorMessage(e, "Failed to add member");
      if (message.toLowerCase().includes("already a member")) {
        handleAlreadyMemberError();
        return;
      }
      setError(message);
    } finally {
      setPendingAction(null);
    }
  };

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      minWidth="480px"
      maxWidth="520px"
      minHeight="auto"
    >
      <div className={confirmStyles.confirmModalBody}>
        <h3 className={confirmStyles.confirmModalTitle}>{tx("pro.users.addMember")}</h3>
        <p className={confirmStyles.confirmModalText}>
          Add <strong>{userName}</strong> to the following organization
        </p>

        {error && <Callout variant="error">{error}</Callout>}
        {success && <Callout variant="success">{success}</Callout>}

        <OrganizationPickerSelect
          label={t("pro.users.organization")}
          value={orgId}
          onChange={(nextOrgId) => {
            setOrgId(nextOrgId);
            setError(null);
          }}
          organizations={organizations}
          disabled={loading}
        />

        {alreadyMember && selectedOrg && (
          <Callout variant="info">
            {userName} is already a member of {selectedOrg.name}.
          </Callout>
        )}

        <label className={fieldStyles.field}>
          <span className={fieldStyles.fieldLabel}>{tx("pro.users.role")}</span>
          <select
            className={fieldStyles.roleSelect}
            value={roleId}
            onChange={(e) => setRoleId(e.target.value)}
            disabled={
              loading || rolesLoading || roles.length === 0 || !orgId || alreadyMember
            }
          >
            {!orgId ? (
              <option value="">{t("pro.users.selectOrganizationFirst")}</option>
            ) : roles.length === 0 ? (
              <option value="">
                {rolesLoading ? "Loading roles…" : "No roles available"}
              </option>
            ) : (
              roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.label?.trim() || formatRoleLabel(role.name)}
                </option>
              ))
            )}
          </select>
        </label>

        <div className={confirmStyles.confirmModalActions}>
          <Button
            color="neutral"
            variant="outline"
            size="md"
            disabled={loading}
            onClick={() => setOpen(false)}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            variant={isSuperAdmin ? "outline" : "solid"}
            size="md"
            loading={pendingAction === "invite"}
            disabled={!canSubmit || loading}
            onClick={() => void handleInvite()}
          >
            Invite
          </Button>
          {isSuperAdmin && (
            <Button
              color="primary"
              variant="solid"
              size="md"
              loading={pendingAction === "add"}
              disabled={!canSubmit || loading}
              onClick={() => void handleAddNow()}
            >
              Add now
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}
